import { inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    Router,
    CanActivateFn,
    CanActivateChildFn,
    CanMatchFn,
    Route,
    UrlSegment,
} from '@angular/router';
import { AuthFacadeService } from 'ngpd-merceros-authentication-fe-components';
import {
    AUTHENTICATION_CONFIG,
    AuthenticationConfig,
    AuthenticationService,
    LoginFacadeService,
} from 'ngpd-merceros-authentication-fe-components/core';
import { map } from 'rxjs/operators';

import environment from '../../../environments/environment';

const LOGIN_VERIFY_PATH = '/login-verify';

/**
 * Browser URL is used because the router URL isn't updated until navigation completes.
 * @returns {boolean} true while the auth library is processing tokens on /login-verify
 */
function isLoginVerificationInProgress(): boolean {
    return window.location.pathname.startsWith(LOGIN_VERIFY_PATH);
}

/**
 *
 * @param _router
 */
function checkRedirection(_router: Router): boolean {
    const sessionReturnUrl = sessionStorage.getItem('returnUrl');

    if (sessionReturnUrl) {
        sessionStorage.removeItem('returnUrl');

        _router.navigate([sessionReturnUrl]);

        return false;
    }
    return true;
}

export const AuthGuardActivate: CanActivateFn = () => {
    const _authConfig: AuthenticationConfig = inject(AUTHENTICATION_CONFIG);
    const _authFacade = inject(AuthFacadeService);
    const _authService = inject(AuthenticationService);
    const _router = inject(Router);
    const _loginFacade = inject(LoginFacadeService);

    return _authFacade.fullyAuthenticated$.pipe(
        map((authenticated) => {
            if (authenticated) {
                return authenticated;
            }
            const token = _authService.getSessionToken();
            const refreshToken = _authService.getRefreshToken();

            if (token && refreshToken) {
                return true;
            }

            if (_authConfig.okta?.enabled) {
                _loginFacade.oktaLogin();
            } else {
                _router.navigate([_authConfig.common.loginRoute]);
            }
            return false;
        })
    );
};

export const AuthGuardActivateChild: CanActivateChildFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => AuthGuardActivate(route, state);

export const AuthGuardMatch: CanMatchFn = (route: Route, segments: UrlSegment[]) => {
    const _authConfig: AuthenticationConfig = inject(AUTHENTICATION_CONFIG);
    const _authFacade = inject(AuthFacadeService);
    const _authService = inject(AuthenticationService);
    const _router = inject(Router);
    const _loginFacade = inject(LoginFacadeService);

    const returnUrl = segments?.length
        ? `/${segments.map((segment) => segment.path).join('/')}`
        : null;

    return _authFacade.fullyAuthenticated$.pipe(
        map((authenticated) => {
            if (authenticated) {
                console.log('AuthGuardMatch: User is authenticated');
                return checkRedirection(_router);
            }
            const token = _authService.getSessionToken();
            const refreshToken = _authService.getRefreshToken();

            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            returnUrl && sessionStorage.setItem('returnUrl', returnUrl);

            if (token && refreshToken) {
                return true;
            }

            if (isLoginVerificationInProgress()) {
                return false;
            }

            if (!environment.enableDirectLogin) {
                window.location.href = environment.externalAuthIssuer;
            } else if (_authConfig.okta?.enabled) {
                _loginFacade.oktaLogin();
            } else {
                _router.navigate([_authConfig.common.loginRoute]);
            }
            return false;
        })
    );
};
